import { useSettings } from '../Settings/SettingsProvider';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import React, { useCallback, useMemo } from 'react';
import { isShapeLabelShown } from './isShapeShownOnStartUp';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeClosed } from 'react-icons/pi';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface ShowHideShapeButtonProps {
  shape: GeoJson;
}

export const ShowHideShapeButton = ({ shape }: ShowHideShapeButtonProps) => {
  const { settings, dataUpdatedAt } = useSettings();
  const { addLayer, zoomToLayer, doesLayerExist, removeLayerById } =
    useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();

  const isShapeShown = useMemo(() => {
    return doesLayerExist(shape.properties.id as string);
  }, [doesLayerExist, shape]);

  const isLabelShown = useMemo(() => {
    return isShapeLabelShown(settings, shape.properties.id as string);
  }, [settings, shape.properties.id as string, dataUpdatedAt]);

  const onShow = useCallback(() => {
    const layer = shapeToVectorLayer(shape, displayProjection, dataProjection);
    const name = shape?.properties?.name || 'No name';

    // Add type: 'shape' to properties for context menu detection
    const propertiesWithType = {
      ...shape.properties,
      type: 'shape',
    };

    addLayer(
      layer,
      name,
      true,   // isRemovableFromLayerControl
      true,   // canZoomToFromLayerControl
      true,   // canShowLabel
      propertiesWithType,
      shape.properties.id,
      isLabelShown,
      false,  // clickable - no left-click behavior for shapes
      false,  // hoverEnabled
      'below-ships',  // position
      true    // contextMenuEnabled - enable right-click context menu
    );
    zoomToLayer(layer);
  }, [
    addLayer,
    shape,
    zoomToLayer,
    isLabelShown,
    displayProjection,
    dataProjection,
  ]);

  const onRemove = useCallback(() => {
    removeLayerById(shape.properties.id as string);
  }, [shape, removeLayerById]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={`is-shape-shown-button-${shape.properties.name ?? shape.properties.id ?? 'shape'}`}
          size="smIcon"
          variant="ghost"
          onClick={isShapeShown ? onRemove : onShow}
        >
          {isShapeShown ? (
            <PiEye className="size-4" />
          ) : (
            <PiEyeClosed className="size-4"></PiEyeClosed>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isShapeShown ? 'Remove shape from map' : 'Add shape to map'}
      </TooltipContent>
    </Tooltip>
  );
};
